import React, {FC, useState} from 'react';
import styled, {css} from 'styled-components';

import {useTimeout} from '../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {HandlerOf, MouseEventHandler} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {Layer} from '../../../libs/shared/ui/src/components/layers/layer';

/*
 * Props.
 */

export enum ResizeSeparatorSidesEnum {
  LEFT = 'LEFT',
  RIGHT = 'RIGHT',
}

interface ResizeSeparatorProps {
  /** Side on which the separator is in regard to the component. Default is right. */
  side?: ResizeSeparatorSidesEnum;
  column: number;
  row?: number;
  size: number;
  minSize: number;
  maxSize: number;
  onIsResizingChange?: HandlerOf<boolean>;
  onSizeChange: HandlerOf<number>;
  color?: string;
  hoverColor?: string;
}

/*
 * Style.
 */

interface ResizeSeparatorStyleProps {
  $column: number;
  $row: number;
  $color?: string;
  $isHovering?: boolean;
}

const StyledSeparatorDiv = styled.div<ResizeSeparatorStyleProps>`
  position: relative;
  grid-column: ${(p) => p.$column};
  grid-row: ${(p) => p.$row};
  width: 2px;
  ${(p) => addColorStyles(p)};
`;

function addColorStyles(props: ResizeSeparatorStyleProps) {
  return css`
    background-color: ${(p) =>
      props.$isHovering ? props.$color || p.theme.greys.shade30 : p.theme.appBackground};
  `;
}

const StyledHandleDiv = styled.div`
  position: absolute;
  top: 0;
  left: -3px;
  width: 7px;
  height: 100%;

  background-color: transparent;
  cursor: ew-resize;
`;

const StyledResizeOverlay = styled.div`
  height: 100%;
  cursor: ew-resize;
`;

/*
 * Component.
 */

interface DragState {
  size: number;
  mouseX: number;
}

// ms
const resizeHoverDelay = 250;

export const ResizeSeparator: FC<ResizeSeparatorProps> = (props) => {
  const {side, size, minSize, maxSize, onIsResizingChange, onSizeChange} = props;
  const [dragState, setDragState] = useState<DragState>();
  const [isHovering, setIsHovering] = useState(false);
  const [safeSetTimeout, safeClearTimeout] = useTimeout();

  /*
   * Event handlers.
   */

  const onHandleMouseOver: MouseEventHandler = () => {
    safeSetTimeout(() => setIsHovering(true), resizeHoverDelay);
  };

  const onHandleMouseOut: MouseEventHandler = () => {
    safeClearTimeout();
    setIsHovering(false);
  };

  const onHandleMouseDown: MouseEventHandler = (event) => {
    // Initiate the drag state.
    setDragState({size, mouseX: event.pageX});

    // If needed, notify the parent.
    if (onIsResizingChange) {
      onIsResizingChange(true);
    }
  };

  const onLayerMouseMove: MouseEventHandler = (event) => {
    if (!dragState) {
      return;
    }

    setIsHovering(true);
    const isOnLeftSide = side === ResizeSeparatorSidesEnum.LEFT;
    const offset = isOnLeftSide ? dragState.mouseX - event.pageX : event.pageX - dragState.mouseX;

    const newSize = dragState.size + offset;
    const newSizeConstrained = Math.max(minSize, Math.min(maxSize, newSize));

    onSizeChange(newSizeConstrained);
  };

  const onLayerMouseUp = () => {
    // Reset the state.
    setDragState(undefined);
    setIsHovering(false);

    // If needed, notify the parent.
    if (onIsResizingChange) {
      onIsResizingChange(false);
    }
  };

  /*
   * Render.
   */

  return (
    <>
      <StyledSeparatorDiv
        $color={props.color}
        $isHovering={isHovering}
        $column={props.column}
        $row={props.row || 1}
        onMouseOver={onHandleMouseOver}
        onMouseOut={onHandleMouseOut}
      >
        <StyledHandleDiv onMouseDown={onHandleMouseDown} />
      </StyledSeparatorDiv>
      {dragState && (
        <Layer>
          <StyledResizeOverlay
            onMouseMove={onLayerMouseMove}
            onMouseUp={onLayerMouseUp}
            onMouseLeave={onLayerMouseUp}
          />
        </Layer>
      )}
    </>
  );
};
